import type { PlanChangeResultView, PlanSwitchOptionView } from "@keydials/api-client";
import { formatSubscriptionDate } from "./subscriptionSummary";

/**
 * What the Subscription card says about switching plan (KEY-651 phase 2), kept out of the component so the
 * words can be tested without a browser. The server decides which rungs are on offer and when each would take
 * effect; this only says it.
 */

function money(pence: number): string {
  return (pence / 100).toLocaleString("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: pence % 100 === 0 ? 0 : 2,
  });
}

/** "£17.97 a month · 1 Personal Estate Planner dial · you and up to 3 family members". */
export function switchOptionLabel(option: PlanSwitchOptionView): string {
  const parts = [`${money(option.amountPence)} ${option.period === "Annual" ? "a year" : "a month"}`];
  for (const d of option.includedDefinitions) {
    parts.push(`${d.instances} ${d.name} ${d.instances === 1 ? "dial" : "dials"}`);
  }
  if (option.familySeats) {
    parts.push(`you and up to ${option.familySeats} family ${option.familySeats === 1 ? "member" : "members"}`);
  }
  return parts.join(" · ");
}

/** When the switch happens, and what it does to the card. */
export function switchTiming(option: PlanSwitchOptionView): string {
  return option.takesEffectNow
    ? "This changes straight away. Stripe charges or credits the difference on your card today."
    : "This changes at the end of the period you've already paid for. Nothing changes until then.";
}

/** What to say once the switch has gone through. */
export function switchedNote(result: PlanChangeResultView, name: string): string {
  return result.appliedNow
    ? `You're now on ${name}.`
    : `Your plan changes to ${name} on ${formatSubscriptionDate(result.effectiveFromUtc)}.`;
}
